import { useMemo } from 'react';
import { Waiting, DoubleCheck, Text } from '~components';
import * as Styled from './styled';
import { Props, validatingRetakeStepInteface } from './types';
import { checklistPersonStatus } from './constants';
import { transhipmentSteps } from './utils';
import { RepresentativeDocuments } from '~services/types';
import { Warning } from '~containers/transhipment/PasswordCreation/styled';

const representativeSteps = [
  transhipmentSteps.finalizePersonalData,
  transhipmentSteps.identityDocument,
  transhipmentSteps.retakeSelfie,
  transhipmentSteps.selfie,
  transhipmentSteps.accountPassword,
  transhipmentSteps.validation,
  transhipmentSteps.done,
];

const spouseSteps = [
  transhipmentSteps.finalizePersonalData,
  transhipmentSteps.identityDocument,
  transhipmentSteps.retakeSelfie,
  transhipmentSteps.selfie,
  transhipmentSteps.validation,
  transhipmentSteps.done,
];

const steps = {
  representative: representativeSteps,
  spouse: spouseSteps,
};

const representativeStatusAccountPassword = [
  'validation',
  'done',
  'retake-documents',
];

const representativeStatusFinalizePersonalData = [
  'documents-collect',
  'account-password',
  ...representativeStatusAccountPassword,
];

function statusRemapping(
  step,
  documents: Array<RepresentativeDocuments>,
  status: string
) {
  const documentFound = documents.find((doc) => doc.category === step.category);

  if (!documentFound) {
    if (
      (step.category === transhipmentSteps.finalizePersonalData.category &&
        representativeStatusFinalizePersonalData.includes(status)) ||
      (step.category === transhipmentSteps.accountPassword.category &&
        representativeStatusAccountPassword.includes(status))
    ) {
      return 'VALID';
    }

    return step.status;
  }

  if (step.category !== 'identity') {
    return documentFound.status.toUpperCase();
  }

  if (documentFound.type === 'cnh') {
    return documentFound.status.toUpperCase();
  }

  const identitiesValid = documents
    .filter((doc) => doc.category === step.category)
    .every((doc) => doc.status.toUpperCase() === 'VALID');

  if (documents.filter((doc) => doc.category === step.category).length === 2) {
    return identitiesValid ? 'VALID' : 'INVALID';
  } else {
    return 'WAITING';
  }
}

function setAccountPasswordDoneIfRetakingSelfie({
  status,
  stepsWithCurrentFound,
}: validatingRetakeStepInteface) {
  if (status !== checklistPersonStatus.RETAKE_SELFIE) {
    return stepsWithCurrentFound;
  }

  const stepsValidation = stepsWithCurrentFound.map((step) => {
    if (step.status === checklistPersonStatus.ACCOUNT_PASSWORD) {
      return { ...step, state: 'VALID' };
    }

    return step;
  });

  return stepsValidation;
}

function ListCheck({ type, showIncolumns, status, documents }: Props) {
  const parsedSteps = useMemo(() => {
    const stepsWithCurrentFound = steps[type].map((step) => {
      return {
        ...step,
        state: statusRemapping(step, documents, status),
      };
    });

    const validatedSteps = setAccountPasswordDoneIfRetakingSelfie({
      type,
      status,
      stepsWithCurrentFound,
    });

    const stepsWithNoDescriptionFiltered = validatedSteps.filter(
      (step) => !!step.description
    );

    if (validatedSteps.every((step) => step.status !== status)) {
      return stepsWithNoDescriptionFiltered;
    }

    return stepsWithNoDescriptionFiltered;
  }, [documents, status, type]);

  const renderByStatus = (status) => {
    switch (status) {
      case 'VALID':
        return <DoubleCheck />;

      case 'INVALID':
        return <Warning />;

      default:
        return <Waiting />;
    }
  };

  return (
    <Styled.List showIncolumns={showIncolumns}>
      {parsedSteps.map((step) => (
        <Styled.Item key={step.category}>
          {renderByStatus(step.state)}
          <Text>{step.description}</Text>
        </Styled.Item>
      ))}
    </Styled.List>
  );
}

export default ListCheck;
